/**
 * SectionRenderer - Renders Section, Card, and Collapsible containers.
 *
 * Handles:
 * - SECTION: Simple visual grouping with optional title
 * - CARD: Bordered card with optional title
 * - COLLAPSIBLE: Expandable/collapsible container
 *
 * Ported from the original MyVault application.
 */

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Sheet,
} from "@mui/joy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { CanvasElement, ContainerElementConfig } from "../../../types";
import { ChildrenRenderer } from "../ChildrenRenderer";
import { ElementRenderer } from "../ElementRenderer";
import type { FieldState } from "../CanvasRenderer";

/**
 * Props for SectionRenderer
 */
interface SectionRendererProps {
  /** The container element */
  element: CanvasElement;
  /** All canvas elements (for child lookup) */
  allElements: CanvasElement[];
  /** Mode: edit or runtime */
  mode: "edit" | "runtime";
  /** Field state for form values */
  fieldState?: Record<string, FieldState>;
}

/**
 * SectionRenderer - Renders Section/Card/Collapsible containers.
 */
export function SectionRenderer({
  element,
  allElements,
  mode,
  fieldState = {},
}: SectionRendererProps) {
  const config = element.props as unknown as ContainerElementConfig;
  const containerType = config?.containerType || "SECTION";
  const title = config?.title || element.props?.label;
  const isCollapsible =
    config?.isCollapsible || containerType === "COLLAPSIBLE";
  const [isExpanded, setIsExpanded] = useState(!config?.defaultCollapsed);

  // Render function for children
  const renderElement = (child: CanvasElement, isNested: boolean) => (
    <ElementRenderer
      key={child.id}
      element={child}
      allElements={allElements}
      isNested={isNested}
      mode={mode}
      fieldState={fieldState}
    />
  );

  // Render children content
  const renderContent = () => (
    <ChildrenRenderer
      elements={allElements}
      parentId={element.id}
      columns={config?.internalGrid?.columns}
      gap={config?.internalGrid?.gap}
      rowHeight={config?.internalGrid?.rowHeight}
      internalGrid={config?.internalGrid}
      mode={mode}
      fieldState={fieldState}
      renderElement={renderElement}
      showEmptyPlaceholder={mode === "edit"}
      emptyPlaceholderText={
        mode === "edit"
          ? `Drop elements into this ${containerType.toLowerCase()}`
          : undefined
      }
    />
  );

  // CARD variant
  if (containerType === "CARD") {
    return (
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {(title || isCollapsible) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography level="title-sm" fontWeight="lg">
              {title || "Card"}
            </Typography>
            {isCollapsible && (
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        )}
        {(!isCollapsible || isExpanded) && (
          <CardContent sx={{ flexGrow: 1, p: 2 }}>
            {renderContent()}
          </CardContent>
        )}
      </Card>
    );
  }

  // COLLAPSIBLE variant
  if (containerType === "COLLAPSIBLE") {
    return (
      <Sheet
        variant="soft"
        sx={{
          borderRadius: "md",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            cursor: "pointer",
            "&:hover": { bgcolor: "background.level2" },
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography level="title-sm" fontWeight="lg">
            {title || "Section"}
          </Typography>
          <IconButton size="sm" variant="plain" color="neutral">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        {isExpanded && <Box sx={{ p: 2, pt: 0 }}>{renderContent()}</Box>}
      </Sheet>
    );
  }

  // Default SECTION variant
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: mode === "edit" ? "1px dashed" : "none",
        borderColor: "neutral.300",
        borderRadius: "md",
        p: title ? 0 : 1,
      }}
    >
      {title && (
        <Typography
          level="title-sm"
          fontWeight="lg"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ flexGrow: 1, p: title ? 2 : 1 }}>{renderContent()}</Box>
    </Box>
  );
}

export default SectionRenderer;
